import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { api } from '../services/AuthService';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const ProductImageUploadModal = ({
  open,
  onClose,
  onUpload,
  productId,
  title = 'Upload de Imagem do Produto'
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        alert('Escolha uma imagem JPEG, PNG ou WebP.');
        event.target.value = '';
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        alert('A imagem deve ter no máximo 5 MB.');
        event.target.value = '';
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Selecione uma imagem');
      return;
    }
    if (!productId) {
      alert('Produto não identificado. Feche esta janela e tente novamente.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await api.post(`/produtos/${productId}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      
      if (response.status === 200) {
        onUpload(result);
        handleClose();
      } else {
        alert(result.error || 'Falha ao enviar imagem');
      }
    } catch (error) {
      alert(error.response?.data?.error || error.response?.data?.details || 'Falha ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ p: 4, minWidth: 400 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Box
          component="label"
          htmlFor="image-upload-input"
          sx={{
            border: '2px dashed #ccc',
            p: 2,
            mb: 2,
            textAlign: 'center',
            cursor: 'pointer',
            display: 'block'
          }}
        >
          {preview ? (
            <img 
              src={preview} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1 }}
            />
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Arraste uma imagem aqui ou clique para selecionar
              </Typography>
            </Box>
          )}
        </Box>

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id="image-upload-input"
        />

        {selectedFile && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Arquivo selecionado: {selectedFile.name}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={handleUpload}
              disabled={uploading}
              fullWidth
            >
              {uploading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Enviando...
                </>
              ) : (
                'Fazer Upload'
              )}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ProductImageUploadModal;
